import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { useLang } from "@/lib/i18n";

const PRICES = ["$299", "$49", "$99"];

export default function Pricing() {
  const { t } = useLang();
  const scrollToContact = () => scrollToId("#contact");

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 bg-secondary/20 border-y border-border">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mb-16"
        >
          <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-primary mb-5">{t.pricing.eyebrow}</p>
          <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl">
            {t.pricing.heading}
          </h2>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground">
            {t.pricing.sub}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {t.pricing.plans.map((p, i) => {
            const featured = i === 2;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`pricing-card-${i}`}
                className={`relative flex flex-col rounded-2xl border p-8 transition-colors duration-300 ${
                  featured ? "border-primary bg-card" : "border-border bg-card hover:border-primary/40"
                }`}
                style={featured ? { boxShadow: "0 0 60px -25px hsl(174 82% 44% / 0.6)" } : undefined}
              >
                {featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
                    {t.pricing.mostPopular}
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1.5">
                  <span className="font-heading text-5xl font-extrabold tracking-tight text-foreground">{PRICES[i]}</span>
                  <span className="mb-1.5 text-sm text-muted-foreground">{p.cadence}</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{p.blurb}</p>

                <ul className="mt-7 space-y-3.5 flex-1">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3 text-sm text-foreground/90">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Check className="h-3 w-3" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToContact}
                  data-testid={`pricing-cta-${i}`}
                  className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${
                    featured
                      ? "bg-primary text-primary-foreground hover:bg-accent"
                      : "border border-border text-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  {t.pricing.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
